/** Nonferrous metal (scrap) permits — S.C. Code § 16-17-680(C): apply, issue or deny, look up, suspend, revoke, replace, print. */
import { useEffect, useRef, useState } from 'react';
import { PERMIT_STATUS_LABEL, REPLACEMENT_FEE, US_STATES, VEHICLE_COLORS, permitExpires } from '@civilos/domain';
import { api } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { Link, useRouter } from '../shared/router.tsx';
import { Badge, Card, ErrorBox, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { at, day, EmptyState, fileBase64, KPI } from './common.tsx';

const TONE: Record<string, 'ok' | 'warn' | 'bad' | 'neutral' | 'info'> = { active: 'ok', expired: 'warn', suspended: 'warn', revoked: 'bad', denied: 'bad', replaced: 'neutral' };
const fullName = (p: any) => [p.first_name, p.middle_name, p.last_name, p.suffix].filter(Boolean).join(' ');
const holder = (p: any) => (p.kind === 'entity' ? `${p.entity_name} (${fullName(p)})` : fullName(p));

export function PermitsPage() {
  const { navigate } = useRouter();
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQuery(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  const r = useLoad(() => api.get<any>(`/api/permits?q=${encodeURIComponent(query)}&status=${status}`), [query, status]);
  const c = r.data?.counts;
  const manage = r.data?.canManage;
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Records"
        title="Scrap metal permits"
        sub="Permits to transport and sell nonferrous metals (S.C. Code § 16-17-680). No fee; $10 to replace a lost one."
        actions={
          manage && (
            <button className="btn primary" onClick={() => navigate(at('/permits/new'))}>
              New application
            </button>
          )
        }
      />
      {c && (
        <div className="ops-kpis">
          <KPI n={c.active} label="Active" tone="ok" on={status === 'active'} onClick={() => setStatus(status === 'active' ? '' : 'active')} />
          <KPI n={c.expiring} label="Expire in 60 days" tone={c.expiring ? 'warn' : undefined} on={status === 'expiring'} onClick={() => setStatus(status === 'expiring' ? '' : 'expiring')} />
          <KPI n={c.expired} label="Expired" on={status === 'expired'} onClick={() => setStatus(status === 'expired' ? '' : 'expired')} />
          <KPI n={c.pulled} label="Suspended / revoked" tone={c.pulled ? 'bad' : undefined} on={status === 'revoked'} onClick={() => setStatus(status === 'revoked' ? '' : 'revoked')} />
          <KPI n={c.denied} label="Denied" on={status === 'denied'} onClick={() => setStatus(status === 'denied' ? '' : 'denied')} />
        </div>
      )}
      <div className="filterbar">
        <input type="search" className="rp-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, permit #, driver’s license # or tag" aria-label="Search permits" autoFocus />
      </div>
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.permits.length ? (
        <EmptyState icon="recycle" title={!manage && !query ? 'Look up a permit' : 'No permits found'}>
          {!manage && !query ? 'Type the seller’s name, permit number, license number or tag.' : undefined}
        </EmptyState>
      ) : (
        <div className="permit-list">
          {r.data.permits.map((p: any) => (
            <button key={p.id} type="button" className="card permit-row" onClick={() => navigate(at(`/permits/${p.id}`))}>
              {p.photo_id ? <img src={`/api/permits/files/${p.photo_id}`} alt="" className="permit-thumb" /> : <span className="permit-thumb none" />}
              <span>
                <strong>{holder(p)}</strong>
                <span className="small muted">
                  {' '}
                  · DL {p.id_number} {p.id_state}
                </span>
                <div className="small">
                  {p.address}, {p.city} {p.state}
                </div>
                {p.vehicle?.tag && <div className="small muted">Vehicle: {[p.vehicle.year, p.vehicle.color, p.vehicle.make, p.vehicle.model].filter(Boolean).join(' ')} · {p.vehicle.tag}</div>}
              </span>
              <span className="permit-side">
                <span className="mono">{p.number ?? '—'}</span>
                <Badge tone={TONE[p.state_now]} dot>
                  {PERMIT_STATUS_LABEL[p.state_now as keyof typeof PERMIT_STATUS_LABEL]}
                </Badge>
                {p.expires_on && <span className="small muted">Expires {day(p.expires_on)}</span>}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ImagePick({ label, hint, value, onChange, capture }: { label: string; hint: string; value: File | null; onChange: (f: File | null) => void; capture?: 'user' | 'environment' }) {
  const ref = useRef<HTMLInputElement>(null);
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!value) return setUrl(null);
    const u = URL.createObjectURL(value);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [value]);
  return (
    <div className="img-pick">
      <span className="lbl">{label}</span>
      <button type="button" className="img-box" onClick={() => ref.current?.click()}>
        {url && value?.type.startsWith('image/') ? <img src={url} alt={label} /> : value ? <span>{value.name}</span> : <span className="muted small">{hint}</span>}
      </button>
      <input ref={ref} type="file" accept="image/jpeg,image/png,application/pdf" capture={capture} hidden aria-label={label} onChange={(e) => onChange(e.target.files?.[0] ?? null)} />
    </div>
  );
}

export function NewPermit() {
  const { navigate, path } = useRouter();
  const fromId = new URLSearchParams(path.split('?')[1] ?? '').get('from');
  const prev = useLoad(() => (fromId ? api.get<any>(`/api/permits/${fromId}`) : Promise.resolve(null)), [fromId]);
  const blank = { kind: 'individual', entityName: '', lastName: '', firstName: '', middleName: '', suffix: '', dob: '', sex: '', race: '', height: '', weight: '', hair: '', eyes: '', address: '', city: '', state: 'SC', zip: '', phone: '', email: '', idType: 'Driver’s license', idNumber: '', idState: 'SC', resident: true };
  const [f, setF] = useState<Record<string, any>>(blank);
  const [veh, setVeh] = useState<Record<string, string>>({ year: '', make: '', model: '', color: '', tag: '', tagState: 'SC', vin: '', owner: '' });
  const [photo, setPhoto] = useState<File | null>(null);
  const [idCopy, setIdCopy] = useState<File | null>(null);
  const [knows, setKnows] = useState(false);
  const [noConv, setNoConv] = useState(false);
  const [bg, setBg] = useState(false);
  const [signed, setSigned] = useState('');
  const [deny, setDeny] = useState<string | null>(null);
  const a = useAction();
  useEffect(() => {
    const p = prev.data?.permit;
    if (!p) return;
    setF({
      ...blank, kind: p.kind, entityName: p.entity_name ?? '', lastName: p.last_name, firstName: p.first_name, middleName: p.middle_name ?? '', suffix: p.suffix ?? '', dob: p.dob ?? '', sex: p.sex ?? '', race: p.race ?? '',
      height: p.height ?? '', weight: p.weight ?? '', hair: p.hair ?? '', eyes: p.eyes ?? '', address: p.address, city: p.city, state: p.state, zip: p.zip, phone: p.phone ?? '', email: p.email ?? '', idType: p.id_type ?? 'Driver’s license',
      idNumber: p.id_number, idState: p.id_state, resident: p.resident,
    });
    setVeh({ year: '', make: '', model: '', color: '', tag: '', tagState: 'SC', vin: '', owner: '', ...p.vehicle });
  }, [prev.data]);
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });
  const sv = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setVeh({ ...veh, [k]: e.target.value });
  const today = new Date().toLocaleDateString('en-CA');
  let exp: string | null = null;
  try {
    exp = f.kind === 'entity' || f.dob ? permitExpires(f.kind, today, f.dob || null) : null;
  } catch {
    exp = null;
  }
  const submit = (decision: 'issue' | 'deny') =>
    a.run(async () => {
      const r = await api.post<any>('/api/permits', {
        ...f, dob: f.dob || null, resident: f.resident, vehicle: veh, decision, denyReason: deny ?? undefined,
        declaration: { signedName: signed, knowsLaw: knows, noConviction: noConv, backgroundChecked: bg },
        photoBase64: photo ? await fileBase64(photo) : null,
        idCopyBase64: idCopy ? await fileBase64(idCopy) : null,
      });
      navigate(at(`/permits/${r.id}`), true);
    });
  return (
    <div className="ops stack permit-form">
      <PageHeader crumb={<Link to={at('/permits')}>Scrap metal permits</Link>} title={fromId ? 'Renew a permit' : 'New permit application'} sub="Permit to transport and sell nonferrous metals — S.C. Code § 16-17-680(C)" />
      <Card title="Applicant">
        <div className="seg-ctl" role="radiogroup" aria-label="Who is applying">
          {(
            [
              ['individual', 'An individual'],
              ['entity', 'A business'],
            ] as const
          ).map(([k, l]) => (
            <button key={k} type="button" role="radio" aria-checked={f.kind === k} className={f.kind === k ? 'on' : ''} onClick={() => setF({ ...f, kind: k })}>
              {l}
            </button>
          ))}
        </div>
        {f.kind === 'entity' && (
          <label>
            Business name
            <input value={f.entityName} onChange={set('entityName')} />
            <span className="hint">Below: the employee who will haul and sell for the business.</span>
          </label>
        )}
        <div className="grid-4">
          <label>
            Last name
            <input value={f.lastName} onChange={set('lastName')} />
          </label>
          <label>
            First name
            <input value={f.firstName} onChange={set('firstName')} />
          </label>
          <label>
            Middle
            <input value={f.middleName} onChange={set('middleName')} />
          </label>
          <label>
            Suffix
            <input value={f.suffix} onChange={set('suffix')} placeholder="Jr." />
          </label>
          <label>
            Date of birth
            <input type="date" value={f.dob} onChange={set('dob')} />
          </label>
          <label>
            Sex
            <select value={f.sex} onChange={set('sex')}>
              <option value="">—</option>
              <option value="M">Male</option>
              <option value="F">Female</option>
            </select>
          </label>
          <label>
            Race
            <select value={f.race} onChange={set('race')}>
              <option value="">—</option>
              <option value="W">White</option>
              <option value="B">Black</option>
              <option value="A">Asian</option>
              <option value="I">American Indian / Alaska Native</option>
              <option value="P">Native Hawaiian / Pacific Islander</option>
              <option value="U">Unknown</option>
            </select>
          </label>
          <label>
            Height
            <input value={f.height} onChange={set('height')} placeholder="5-10" />
          </label>
          <label>
            Weight
            <input value={f.weight} onChange={set('weight')} inputMode="numeric" />
          </label>
          <label>
            Hair
            <input value={f.hair} onChange={set('hair')} placeholder="BRO" />
          </label>
          <label>
            Eyes
            <input value={f.eyes} onChange={set('eyes')} placeholder="BLU" />
          </label>
          <label>
            Phone
            <input value={f.phone} onChange={set('phone')} inputMode="tel" />
          </label>
        </div>
        <div className="grid-4">
          <label className="span-2">
            Street address
            <input value={f.address} onChange={set('address')} />
          </label>
          <label>
            City
            <input value={f.city} onChange={set('city')} />
          </label>
          <label>
            State / ZIP
            <span className="row gap-sm">
              <select value={f.state} onChange={set('state')} aria-label="State">
                {US_STATES.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
              <input value={f.zip} onChange={set('zip')} inputMode="numeric" aria-label="ZIP" />
            </span>
          </label>
          <label>
            ID type
            <select value={f.idType} onChange={set('idType')}>
              <option>Driver’s license</option>
              <option>State ID card</option>
              <option>Passport</option>
              <option>Military ID</option>
            </select>
          </label>
          <label>
            ID number
            <input className="mono" value={f.idNumber} onChange={set('idNumber')} />
          </label>
          <label>
            ID state
            <select value={f.idState} onChange={set('idState')}>
              {US_STATES.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </label>
          <label className="check">
            <input type="checkbox" checked={f.resident} onChange={(e) => setF({ ...f, resident: e.target.checked })} /> Lives (or business is located) in this county
          </label>
        </div>
      </Card>
      <Card title="Vehicle used to haul">
        <div className="grid-4">
          <label>
            Year
            <input value={veh.year} onChange={sv('year')} inputMode="numeric" />
          </label>
          <label>
            Make
            <input value={veh.make} onChange={sv('make')} />
          </label>
          <label>
            Model
            <input value={veh.model} onChange={sv('model')} />
          </label>
          <label>
            Color
            <select value={veh.color} onChange={sv('color')}>
              <option value="">—</option>
              {VEHICLE_COLORS.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </label>
          <label>
            Tag
            <input className="mono" value={veh.tag} onChange={(e) => setVeh({ ...veh, tag: e.target.value.toUpperCase() })} />
          </label>
          <label>
            Tag state
            <select value={veh.tagState} onChange={sv('tagState')}>
              {US_STATES.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </label>
          <label className="span-2">
            VIN
            <input className="mono" value={veh.vin} onChange={(e) => setVeh({ ...veh, vin: e.target.value.toUpperCase() })} />
          </label>
          <label className="span-2">
            Registered owner (if not the applicant)
            <input value={veh.owner} onChange={sv('owner')} />
          </label>
        </div>
      </Card>
      <Card title="Photograph and ID copy (the sheriff must keep both)">
        <div className="row gap wrap">
          <ImagePick label="Photograph of the applicant" hint="Tap to take or choose a photo" value={photo} onChange={setPhoto} capture="user" />
          <ImagePick label="Copy of the ID" hint="Tap to photograph or scan the license" value={idCopy} onChange={setIdCopy} capture="environment" />
        </div>
        {fromId && prev.data?.photoId && !photo && <p className="small muted">A renewal needs a current photo and ID copy.</p>}
      </Card>
      <Card title="Declaration">
        <label className="check">
          <input type="checkbox" checked={knows} onChange={(e) => setKnows(e.target.checked)} /> The applicant knows § 16-17-680 and will comply with it.
        </label>
        <label className="check">
          <input type="checkbox" checked={noConv} onChange={(e) => setNoConv(e.target.checked)} /> The applicant has not been convicted under § 16-11-523 or § 16-17-680.
        </label>
        <label className="check">
          <input type="checkbox" checked={bg} onChange={(e) => setBg(e.target.checked)} /> Background checked (optional — the sheriff may investigate before issuing)
        </label>
        <label>
          Applicant’s signature (typed name)
          <input value={signed} onChange={(e) => setSigned(e.target.value)} placeholder={[f.firstName, f.lastName].filter(Boolean).join(' ')} />
        </label>
      </Card>
      <div className="card permit-decide">
        <div>
          {exp ? (
            <>
              Issued today, this permit expires <strong>{day(exp)}</strong> {f.kind === 'individual' ? '(their birthday, second year after issue)' : '(two years from today)'}. Good statewide. No fee.
            </>
          ) : (
            <span className="muted">Enter the date of birth to see when the permit expires.</span>
          )}
        </div>
        <div className="row gap-sm wrap">
          {deny === null ? (
            <>
              <button className="btn primary" disabled={a.busy} onClick={() => submit('issue')}>
                Issue permit
              </button>
              <button className="btn danger ghost" onClick={() => setDeny('')}>
                Deny…
              </button>
            </>
          ) : (
            <>
              <input value={deny} onChange={(e) => setDeny(e.target.value)} placeholder="Reason for denial" aria-label="Reason for denial" />
              <button className="btn danger" disabled={!deny.trim() || a.busy} onClick={() => submit('deny')}>
                Deny application
              </button>
              <button className="btn ghost" onClick={() => setDeny(null)}>
                Back
              </button>
            </>
          )}
        </div>
        <ErrorBox error={a.error} />
      </div>
    </div>
  );
}

export function PermitDetail({ id }: { id: string }) {
  const { navigate } = useRouter();
  const { me } = useAuth();
  const r = useLoad(() => api.get<any>(`/api/permits/${id}`), [id]);
  const [act, setAct] = useState<'suspend' | 'reinstate' | 'revoke' | 'replace' | null>(null);
  const [reason, setReason] = useState('');
  const [paid, setPaid] = useState(true);
  const a = useAction();
  if (r.error) return <ErrorBox error={r.error} />;
  if (!r.data) return <Spinner />;
  const p = r.data.permit;
  const state = p.state_now as string;
  const manage = r.data.canManage;
  const go = () =>
    a.run(async () => {
      if (act === 'replace') {
        const x = await api.post<any>(`/api/permits/${id}/replace`, { feePaid: paid, reason });
        navigate(at(`/permits/${x.id}`));
      } else await api.post(`/api/permits/${id}/status`, { action: act, reason });
      setAct(null);
      setReason('');
      r.reload();
    });
  return (
    <div className="ops stack">
      <PageHeader
        crumb={<Link to={at('/permits')}>Scrap metal permits</Link>}
        title={p.number ? `Permit ${p.number}` : 'Denied application'}
        sub={holder(p)}
        actions={
          <>
            {state === 'active' && (
              <button className="btn" onClick={() => window.print()}>
                Print permit
              </button>
            )}
            {manage && (state === 'expired' || (state === 'active' && p.expires_on <= new Date(Date.now() + 60 * 86_400_000).toLocaleDateString('en-CA'))) && (
              <button className="btn primary" onClick={() => navigate(at(`/permits/new?from=${id}`))}>
                Renew
              </button>
            )}
          </>
        }
      />
      <div className={`alert ${state === 'active' ? 'ok' : state === 'expired' || state === 'suspended' ? 'warn' : 'bad'}`}>
        <strong>{PERMIT_STATUS_LABEL[state as keyof typeof PERMIT_STATUS_LABEL]}</strong>
        {p.expires_on && ` · ${state === 'expired' ? 'expired' : 'expires'} ${day(p.expires_on)}`}
        {p.status_reason && ` — ${p.status_reason}`}
      </div>
      <div className="permit-card print-area" aria-label="Permit">
        <div className="pc-head">
          <strong>{me!.agency.name}</strong>
          <span>Permit to Transport and Sell Nonferrous Metals · S.C. Code § 16-17-680</span>
        </div>
        <div className="pc-body">
          {r.data.photoId ? <img src={`/api/permits/files/${r.data.photoId}`} alt="Permit holder" className="pc-photo" /> : <span className="pc-photo none">No photo</span>}
          <dl className="kv">
            <dt>Permit #</dt>
            <dd className="mono">{p.number ?? '—'}</dd>
            {p.kind === 'entity' && (
              <>
                <dt>Business</dt>
                <dd>{p.entity_name}</dd>
              </>
            )}
            <dt>{p.kind === 'entity' ? 'Employee' : 'Name'}</dt>
            <dd>{fullName(p)}</dd>
            <dt>Address</dt>
            <dd>
              {p.address}, {p.city}, {p.state} {p.zip}
            </dd>
            <dt>DOB</dt>
            <dd>{day(p.dob)}</dd>
            <dt>Description</dt>
            <dd>{[p.sex, p.race, p.height && `${p.height}`, p.weight && `${p.weight} lb`, p.hair && `hair ${p.hair}`, p.eyes && `eyes ${p.eyes}`].filter(Boolean).join(' · ') || '—'}</dd>
            <dt>{p.id_type ?? 'ID'}</dt>
            <dd className="mono">
              {p.id_number} ({p.id_state})
            </dd>
            <dt>Issued</dt>
            <dd>{day(p.issued_on)}</dd>
            <dt>Expires</dt>
            <dd>
              <strong>{day(p.expires_on)}</strong>
            </dd>
          </dl>
        </div>
        {p.vehicle && Object.keys(p.vehicle).length > 0 && (
          <div className="pc-veh small">
            Vehicle: {[p.vehicle.year, p.vehicle.color, p.vehicle.make, p.vehicle.model].filter(Boolean).join(' ')} {p.vehicle.tag && `· Tag ${p.vehicle.tag} ${p.vehicle.tagState ?? ''}`} {p.vehicle.vin && `· VIN ${p.vehicle.vin}`}
          </div>
        )}
        <div className="pc-foot small">Valid statewide. Must be shown to the secondary metals recycler at each sale.</div>
      </div>
      {manage && (
        <Card title="Actions">
          <div className="row gap-sm wrap">
            {state === 'active' && (
              <button className={`btn ${act === 'replace' ? 'primary' : ''}`} onClick={() => setAct('replace')}>
                Replace (lost / destroyed)
              </button>
            )}
            {state === 'active' && (
              <button className={`btn ${act === 'suspend' ? 'primary' : ''}`} onClick={() => setAct('suspend')}>
                Suspend
              </button>
            )}
            {state === 'suspended' && (
              <button className={`btn ${act === 'reinstate' ? 'primary' : ''}`} onClick={() => setAct('reinstate')}>
                Reinstate
              </button>
            )}
            {(state === 'active' || state === 'suspended') && (
              <button className={`btn danger ghost ${act === 'revoke' ? 'on' : ''}`} onClick={() => setAct('revoke')}>
                Revoke
              </button>
            )}
            <label className="btn ghost">
              New photo
              <input type="file" accept="image/*" capture="user" hidden onChange={async (e) => e.target.files?.[0] && (await api.post(`/api/permits/${id}/files`, { kind: 'photo', imageBase64: await fileBase64(e.target.files[0]) }), r.reload())} />
            </label>
          </div>
          {act && (
            <div className="row gap-sm wrap" style={{ marginTop: 10 }}>
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={act === 'replace' ? 'What happened (lost wallet…)' : act === 'reinstate' ? 'Why it’s reinstated' : 'Reason — inaccurate application, not complying, or conviction'} aria-label="Reason" />
              {act === 'replace' && (
                <label className="check">
                  <input type="checkbox" checked={paid} onChange={(e) => setPaid(e.target.checked)} /> ${REPLACEMENT_FEE} replacement fee paid
                </label>
              )}
              <button className={`btn ${act === 'revoke' ? 'danger' : 'primary'}`} disabled={a.busy || (act !== 'replace' && !reason.trim())} onClick={go}>
                {act === 'replace' ? 'Issue replacement' : act === 'suspend' ? 'Suspend permit' : act === 'revoke' ? 'Revoke permit' : 'Reinstate'}
              </button>
            </div>
          )}
          <ErrorBox error={a.error} />
        </Card>
      )}
      <div className="grid-2">
        {r.data.idCopyId && (
          <Card title="ID copy on file">
            <img src={`/api/permits/files/${r.data.idCopyId}`} alt="ID copy" className="id-copy" />
          </Card>
        )}
        <Card title="History">
          <ol className="timeline compact">
            {r.data.events.map((e: any, i: number) => (
              <li key={i}>
                <span className="tl-dot" aria-hidden />
                <div>
                  <strong>{e.action.charAt(0).toUpperCase() + e.action.slice(1)}</strong>
                  {e.note && <div className="small">{e.note}</div>}
                  <div className="small muted">
                    {day(e.at)} · {e.by_name}
                  </div>
                </div>
              </li>
            ))}
          </ol>
          {r.data.related.length > 0 && (
            <p className="small">
              Related:{' '}
              {r.data.related.map((x: any) => (
                <Link key={x.id} to={at(`/permits/${x.id}`)}>
                  {x.number ?? 'application'} ({x.status}){' '}
                </Link>
              ))}
            </p>
          )}
        </Card>
      </div>
    </div>
  );
}
